import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useChatService } from '../../services';
import ChatHistorySection from './ChatHistorySection';
import { ChatHistory } from './types';

interface ChatHistoryDropdownProps {
  isVisible: boolean;
  position: { top: number; left: number };
  onClose: () => void;
  onChatClick: (chatId: number) => void;
}

const ChatHistoryDropdown: React.FC<ChatHistoryDropdownProps> = ({
  isVisible,
  position,
  onClose,
  onChatClick,
}) => {
  const [historySearch, setHistorySearch] = useState("");
  const [dropdownSearchResults, setDropdownSearchResults] = useState<any[]>([]);
  const [isDropdownSearching, setIsDropdownSearching] = useState(false);
  const [dropdownSearchPage, setDropdownSearchPage] = useState(1);
  const [hasMoreDropdownResults, setHasMoreDropdownResults] = useState(true);

  const dropdownObserverRef = useRef<HTMLDivElement>(null);

  const { 
    chatHistoryByDate, 
    pinnedChats, 
    isLoadingMore, 
    hasMoreChats, 
    currentPage 
  } = useSelector((state: any) => state.chatMessages);

  const { getChatHistoryList, searchChatHistory } = useChatService();

  const handleDropdownSearch = useCallback(async (searchTerm: string, page: number = 1, append: boolean = false) => {
    if (!append) {
      setIsDropdownSearching(true);
      setDropdownSearchPage(1);
    }
    
    try {
      const result = await searchChatHistory(searchTerm, page, 20, append);
      
      if (result) {
        const newResults = [
          ...(result.today || []).map((chat: any) => ({ ...chat, section: 'Today' })),
          ...(result.yesterday || []).map((chat: any) => ({ ...chat, section: 'Yesterday' })),
          ...(result.older || []).map((chat: any) => ({ ...chat, section: 'Older' }))
        ];

        if (append) {
          const existingIds = new Set(dropdownSearchResults.map(c => c.chat_id));
          const uniqueNewResults = newResults.filter(c => !existingIds.has(c.chat_id));
          setDropdownSearchResults(prev => [...prev, ...uniqueNewResults]);
        } else {
          setDropdownSearchResults(newResults);
        }
        
        setHasMoreDropdownResults(result.hasMore || false);
        if (append) {
          setDropdownSearchPage(page);
        }
      }
    } catch (error) {
      console.error('Error searching dropdown chats:', error);
    } finally {
      if (!append) {
        setIsDropdownSearching(false);
      }
    }
  }, [searchChatHistory, dropdownSearchResults]);

  const handleLoadMoreDropdownSearch = useCallback(() => {
    if (isDropdownSearching || !hasMoreDropdownResults) return;
    handleDropdownSearch(historySearch, dropdownSearchPage + 1, true);
  }, [isDropdownSearching, hasMoreDropdownResults, historySearch, dropdownSearchPage, handleDropdownSearch]);

  const handleLoadMoreDropdownChats = useCallback(async () => {
    if (isLoadingMore || !hasMoreChats) return;
    
    try {
      await getChatHistoryList(null, {
        page: currentPage + 1,
        per_page: 20,
        append: true
      });
    } catch (error) {
      console.error('Error loading more dropdown chats:', error);
    }
  }, [isLoadingMore, hasMoreChats, getChatHistoryList, currentPage]);

  useEffect(() => {
    if (!isVisible) return;
    
    const timeoutId = setTimeout(() => {
      if (historySearch.trim()) {
        handleDropdownSearch(historySearch, 1);
        setDropdownSearchPage(1);
      } else {
        setDropdownSearchResults([]);
        setHasMoreDropdownResults(true);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [historySearch, isVisible, handleDropdownSearch]);

  useEffect(() => {
    if (!dropdownObserverRef.current || !isVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (historySearch.trim()) {
            if (hasMoreDropdownResults && !isDropdownSearching) {
              handleLoadMoreDropdownSearch();
            }
          } else if (hasMoreChats && !isLoadingMore) {
            handleLoadMoreDropdownChats();
          }
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(dropdownObserverRef.current);
    return () => observer.disconnect();
  }, [
    isVisible,
    historySearch,
    hasMoreDropdownResults,
    isDropdownSearching,
    hasMoreChats,
    isLoadingMore,
    handleLoadMoreDropdownSearch,
    handleLoadMoreDropdownChats
  ]);

  useEffect(() => {
    if (!isVisible) {
      setHistorySearch("");
      setDropdownSearchResults([]);
      setDropdownSearchPage(1);
      setHasMoreDropdownResults(true);
      setIsDropdownSearching(false);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const renderContent = () => {
    if (historySearch.trim() && dropdownSearchResults.length > 0) {
      const groupedResults = {
        today: dropdownSearchResults.filter(chat => chat.section === 'Today'),
        yesterday: dropdownSearchResults.filter(chat => chat.section === 'Yesterday'),
        older: dropdownSearchResults.filter(chat => chat.section === 'Older')
      };

      return (
        <>
          <ChatHistorySection
            label="Pinned"
            items={pinnedChats}
            onChatClick={onChatClick}
            defaultExpanded={false}
          />
          {groupedResults.today.length > 0 && (
            <ChatHistorySection
              label="Today"
              items={groupedResults.today}
              onChatClick={onChatClick}
            />
          )}
          {groupedResults.yesterday.length > 0 && (
            <ChatHistorySection
              label="Yesterday"
              items={groupedResults.yesterday}
              onChatClick={onChatClick}
            />
          )}
          {groupedResults.older.length > 0 && (
            <ChatHistorySection
              label="Older"
              items={groupedResults.older}
              onChatClick={onChatClick}
              showLoadMore={hasMoreDropdownResults}
              isLoadingMore={isDropdownSearching}
              onLoadMore={handleLoadMoreDropdownSearch}
              sentinelClassName="px-2 py-2 pb-56"
            />
          )}
          {hasMoreDropdownResults && (
            <div ref={dropdownObserverRef} className="h-1" />
          )}
        </>
      );
    } else if (!historySearch.trim()) {
      const filterFn = (arr: ChatHistory[]) => {
        return arr.filter((h: ChatHistory) => h.title.toLowerCase().includes(historySearch.toLowerCase()))
      };

      const filteredToday = filterFn(chatHistoryByDate.today);
      const filteredYesterday = filterFn(chatHistoryByDate.yesterday);
      const filteredOlder = filterFn(chatHistoryByDate.older);

      return (
        <>
          <ChatHistorySection
            label="Pinned"
            items={pinnedChats}
            onChatClick={onChatClick}
            defaultExpanded={false}
          />
          <ChatHistorySection
            label="Today"
            items={filteredToday}
            onChatClick={onChatClick}
          />

          {filteredYesterday.length > 0 && (
            <ChatHistorySection
              label="Yesterday"
              items={filteredYesterday}
              onChatClick={onChatClick}
            />
          )}

          {filteredOlder.length > 0 && (
            <ChatHistorySection
              label="Older"
              items={filteredOlder}
              onChatClick={onChatClick}
              showLoadMore={hasMoreChats}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMoreDropdownChats}
              sentinelClassName="px-2  pb-10"
            />
          )}
          {hasMoreChats && (
            <div ref={dropdownObserverRef} className="h-1" />
          )}
        </>
      );
    } else if (isDropdownSearching) {
      return (
        <div className="flex items-center justify-center py-4">
          <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm text-gray-500 dark:text-gray-400">Searching...</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center py-4 text-sm text-gray-500 dark:text-gray-400">
          No chats found
        </div>
      );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <button 
        className="fixed inset-0 z-[99990] bg-transparent border-0 cursor-default" 
        onClick={onClose}
        aria-label="Close dropdown"
      />
      
      {/* Dropdown */}
      <div
        style={{ top: position.top, left: position.left }}
        className="fixed z-[100000] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg w-72 max-h-80 overflow-auto"
      >
        <div className="p-2 space-y-2">
          {/* Search input */}
          {/* <input
            type="text"
            placeholder="Search chats..."
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          /> */}

          {/* Content */}
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default ChatHistoryDropdown;
